import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductListOfStore } from "../../redux/Thunk/Product/ProductThunks";
import { searchProduct as searchProducts } from "../../redux/Thunk/Product/ProductThunks";
import Pagination from "../common/Pagination";
import { setSearchProducts } from "../../redux/Reducer/Product/ProductSlice";
import {
  setActiveForm,
  setActiveTab,
} from "../../redux/Reducer/Admin/PartnerSlice";
import AdminTable from "../common/Table";
import Loader from "../Loader";
import { Tooltip } from "../common/Tooltip";
import ProductTagsSelector from "../Admin/Inventory/ProductTagsSelector";

function StoreStock() {
  const dispatch = useDispatch();
  const { storeProducts, searchProduct, loading } = useSelector(
    (state) => state?.product
  );
  const [productList, setProductList] = useState([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    dispatch(setSearchProducts([]));
  }, [dispatch]);

  const tagIdsString = useMemo(() => {
    return selectedFilterTags
      .map((t) => (typeof t === "object" ? t.id : t))
      .filter(Boolean)
      .join(",");
  }, [selectedFilterTags]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      dispatch(
        fetchProductListOfStore({ page: currentPage, limit, search: "", tagIds: tagIdsString })
      );
    }, 100);

    return () => clearTimeout(debounceTimeout);
  }, [dispatch, currentPage, limit, tagIdsString]);

  useEffect(() => {
    let filteredProducts = storeProducts?.data || [];

    if (searchProduct?.length > 0) {
      const searchProductIIDs = searchProduct?.map((item) =>
        item?.IID?.toLowerCase()
      );

      filteredProducts = filteredProducts?.filter((product) =>
        searchProductIIDs.includes(product?.product?.IID?.toLowerCase())
      );
    }

    if (selectedFilterTags?.length > 0) {
      filteredProducts = filteredProducts?.filter((item) => {
        const productTags = item?.product?.tags || [];
        return selectedFilterTags.some((sTag) => {
          const sId = typeof sTag === "object" ? sTag.id : sTag;
          const sName = (typeof sTag === "object" ? sTag.name : String(sTag))?.toUpperCase();
          return productTags.some((pTag) => {
            const pId = typeof pTag === "object" ? pTag.id : pTag;
            const pName = (typeof pTag === "object" ? pTag.name : String(pTag))?.toUpperCase();
            return (sId && pId && String(sId) === String(pId)) || (sName && pName && sName === pName);
          });
        });
      });
    }

    setProductList(filteredProducts);
  }, [storeProducts, searchProduct, selectedFilterTags]);

  let totalPages = storeProducts?.totalPages;
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleView = React.useCallback(
    async (row) => {
      let name = row?.product?.productName || row?.productName || "";
      let IID = row?.product?.IID || row?.IID || "";

      dispatch(setActiveForm("editProduct"));
      dispatch(setActiveTab({ name: "Products", index: 1 }));
      await dispatch(searchProducts({ name, IID }));
    },
    [dispatch]
  );
  const columns = useMemo(
    () => [
      {
        Header: "Sr.",
        accessor: (row, index) => (currentPage - 1) * limit + index + 1,
        width: "60px",
        Cell: ({ value }) => (
          <span className="font-semibold text-gray-500 text-[12px]">{value}</span>
        ),
      },
      {
        Header: "Product Name & ID",
        accessor: (row) => row,
        Cell: ({ value: row }) => {
          const product = row?.product || row;
          return (
            <div className="flex flex-col min-w-0 py-0.5">
              <button
                type="button"
                onClick={() => handleView(row)}
                className="text-left font-bold text-[13px] text-secondry-900 hover:text-primary-600 hover:underline leading-snug cursor-pointer group inline-flex items-center gap-1.5 transition-colors"
                title="Click to view product details in Products tab"
              >
                <span className="truncate">{product?.productName || "Unnamed Product"}</span>
                <svg
                  className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-0.5 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </button>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="px-1.5 py-0.2 bg-gray-100 text-gray-700 font-mono text-[10px] font-bold rounded border border-gray-200">
                  #{product?.IID}
                </span>
                {product?.type && (
                  <span className="px-1.5 py-0.2 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded border border-blue-100">
                    {product?.type}
                  </span>
                )}
                {product?.lookupMargin?.type && (
                  <span className="px-1.5 py-0.2 bg-purple-50 text-purple-700 text-[10px] font-semibold rounded border border-purple-200">
                    {product.lookupMargin.type}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Store Stock",
        accessor: (row) => row?.storeStock ?? 0,
        width: "130px",
        Cell: ({ value, row }) => {
          const qty = Number(value);
          const uom = row?.product?.uom ? ` (${row.product.uom})` : "";
          let badgeStyle = "bg-emerald-50 text-emerald-700 border-emerald-200";
          let dotColor = "bg-emerald-500";

          if (qty === 0) {
            badgeStyle = "bg-rose-50 text-rose-700 border-rose-200";
            dotColor = "bg-rose-500";
          } else if (qty <= 10) {
            badgeStyle = "bg-amber-50 text-amber-700 border-amber-200";
            dotColor = "bg-amber-500";
          }

          return (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${badgeStyle}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
              {qty > 0 ? `${qty}${uom}` : "Out of Stock"}
            </span>
          );
        },
      },
      {
        Header: "Pricing",
        accessor: (row) => row?.product,
        width: "130px",
        Cell: ({ value: product }) => {
          const mrp = product?.pricing?.mrp;
          const gst = product?.pricing?.gst;
          const discount = product?.lookupMargin?.discount ?? product?.discount;
          return (
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-[13px] text-secondry-900 leading-tight">
                {mrp ? `₹${mrp}` : "-"}
              </span>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {gst ? (
                  <span className="text-[10px] text-gray-500 font-medium">
                    GST: {gst}%
                  </span>
                ) : null}
                {discount !== undefined && discount !== null ? (
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-100">
                    {discount}% Off
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Tags",
        accessor: (row) => row?.product?.tags,
        width: "160px",
        Cell: ({ value }) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return <span className="text-gray-400 text-[11px] italic">None</span>;
          }
          return (
            <div className="flex flex-wrap gap-1 items-center">
              {value.map((t, idx) => {
                const name = typeof t === "object" ? t?.name : String(t);
                const color = typeof t === "object" ? t?.color : "#3B82F6";
                return (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white inline-block shadow-2xs whitespace-nowrap"
                    style={{ backgroundColor: color || "#3B82F6" }}
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        Header: "Manufacturer",
        accessor: (row) => row?.product?.manufacturer,
        width: "140px",
        Cell: ({ value }) => (
          <Tooltip>
            <span className="text-[12px] text-gray-700 font-medium truncate block max-w-[130px]">
              {value || "-"}
            </span>
            <span className="tooltip-text">{value}</span>
          </Tooltip>
        ),
      },
    ],
    [currentPage, limit, handleView]
  );

  const handleResetFilters = () => {
    setSelectedFilterTags([]);
  };

  const isFilterActive = selectedFilterTags.length > 0;

  return (
    <section className="bg-white border border-primary-100 rounded-[12px] shadow-xs overflow-hidden">
      <Loader loading={loading} />

      {/* Top Header & Filter Toolbar */}
      <div className="p-4 bg-gray-50/70 border-b border-primary-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Metric Badges */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-primary-200 rounded-[8px] shadow-2xs">
            <span className="text-[11px] font-bold uppercase text-primary-500 tracking-wider">
              Store Inventory Items:
            </span>
            <span className="text-[13px] font-black text-primary-900 font-mono">
              {storeProducts?.totalItems || productList?.length || 0}
            </span>
          </div>
          {isFilterActive && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-[8px]">
              <span className="text-[11px] font-bold uppercase text-emerald-700 tracking-wider">
                Matching Results:
              </span>
              <span className="text-[13px] font-black text-emerald-900 font-mono">
                {productList?.length || 0}
              </span>
            </div>
          )}
        </div>

        {/* Filter Toolbar */}
        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
          <div className="w-full sm:w-[300px] lg:w-[360px]">
            <ProductTagsSelector
              selectedTags={selectedFilterTags}
              onTagsChange={(tags) => setSelectedFilterTags(tags)}
              label={null}
              placeholder="Filter by tags..."
            />
          </div>
          {isFilterActive && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="text-[12px] font-bold text-rose-600 hover:text-rose-800 underline whitespace-nowrap px-1"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Main Table or Clean Empty State */}
      {productList && productList.length > 0 ? (
        <AdminTable columns={columns} filteredData={productList} />
      ) : (
        <div className="p-12 text-center flex flex-col items-center justify-center bg-white">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center text-primary-500 mb-3 border border-primary-100">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </div>
          <h3 className="text-[15px] font-bold text-secondry-800 mb-1">
            No products found in store stock
          </h3>
          <p className="text-[13px] text-gray-500 max-w-sm mb-4">
            {isFilterActive
              ? "No products match the selected tag filters."
              : "No stock items are assigned to this store."}
          </p>
          {isFilterActive && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-[12px] font-semibold rounded-full transition-all shadow-xs"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Sr.No.</th>
              <th>IID</th>
              <th>Product Name</th>
              <th>Stock</th>
              <th>Units</th>
              <th>MRP</th>
              <th>Disc</th>
              <th>Listing</th>
              <th>Drugs</th>
              <th>Manufacturer</th>
              <th>Type</th>
              <th>GST</th>
            </tr>
          </thead>
          <tbody>
            {productList?.length > 0 &&
              productList?.map((products, index) => {
                let product = products?.product;
                return (
                  <tr>
                    <td>{index + 1}</td>
                    <td>{product?.IID}</td>
                    <td>{product?.productName}</td>
                    <td>{products?.storeStock ? products?.storeStock : 0}</td>

                    <td>{products?.units || 0}</td>

                    <td>{product?.pricing?.mrp}</td>
                    <td>{product?.pricing?.discount}</td>
                    <td>
                      <button
                        onClick={() => handleView(product)}
                        className="underline "
                      >
                        View
                      </button>
                    </td>
                    <td>
                      {product?.drugs?.map((drug, index) => (
                        <React.Fragment key={index}>
                          {drug?.name ? drug?.name : drug}
                          <br />
                        </React.Fragment>
                      ))}
                    </td>
                    <td>{product?.manufacturer}</td>
                    <td>{product?.type}</td>
                    <td>{product?.pricing?.gst}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div> */}
      {/* Pagination Component */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isPreviousDisabled={currentPage === 1}
        isNextDisabled={currentPage === totalPages}
        totalItems={storeProducts?.totalItems}
        itemLength={productList?.length}
        isViewNext={searchProduct?.length > 0 ? true : false}
        limit={limit}
      />
    </section>
  );
}

export default StoreStock;
